import * as phone from '../phone';
import * as pinterest from '../pinterest';
import * as reddit from '../reddit';
import * as uploads from '../uploads';
import { CreateFormSubmissionWithReCaptcha, ReCaptchaError } from './formsubmissions';
import * as models from './models';

export { ReCaptchaError };

export interface CreateContactOpts {
    $reCaptchaPlaceholder: HTMLElement;
    name: string;
    company: string;
    phone: phone.Phone;
    email: string;
    content: string;
    uploader?: uploads.Uploader;
    uploadIDs?: number[];
    preferSMS?: boolean;
    ignoreSMSPreference?: boolean;
}

/**
 * CreateContact submits a brand contact form as a `contact` form submission
 * (#6716). Like quote-request, contact processes in-request server-side
 * (user-if-missing, contact record, notification events), but the page waits
 * on none of it — the resolved response just confirms the submission
 * persisted, no polling. It also runs the shared success behavior (lead
 * tracking).
 *
 * The reCAPTCHA contract is unchanged from the retired /contacts endpoint —
 * see CreateFormSubmissionWithReCaptcha.
 */
export async function CreateContact(opts: CreateContactOpts): Promise<models.FormSubmissionCreateResponse> {
    const createRequest = new models.ContactCreateRequest({
        name: opts.name,
        company: opts.company,
        phone: await opts.phone.format(),
        email: opts.email,
        content: opts.content,
        preferSms: opts.preferSMS,
        ignoreSmspreference: opts.ignoreSMSPreference,
        referrer: location.href,
        tz: new Intl.DateTimeFormat().resolvedOptions().timeZone,
    });

    if (opts.uploadIDs?.length) {
        createRequest.uploadIDs = opts.uploadIDs;
    } else if (opts.uploader) {
        // allowPartial: the message is the payload; failed attachments were already surfaced
        createRequest.uploadIDs = await opts.uploader.getUploadIDs({ allowPartial: true });
    }

    const submission = await CreateFormSubmissionWithReCaptcha({
        form: 'contact',
        payload: createRequest,
        uploadIDs: createRequest.uploadIDs,
    }, {
        $reCaptchaPlaceholder: opts.$reCaptchaPlaceholder,
        action: 'contact',
    });

    // Unqualified (non-optimizing) custom events, not standard Leads — raw
    // contact-form fills must not optimize ad delivery (#7362, same policy
    // as #6790 for quote requests).
    reddit.TrackUnqualifiedLead({
        email: opts.email,
        phoneNumber: createRequest.phone,
        eventId: `contact_${submission.formSubmissionID}`,
    });

    pinterest.TrackUnqualifiedLead({
        email: opts.email,
        leadType: 'Contact Form',
        eventId: `contact_${submission.formSubmissionID}`,
    });

    return submission;
}
